import styled from '@emotion/styled';
import { ImageMessage, VideoBox, VideoMessage } from 'components/Dialogue';
import { useMemo } from 'react';
import { Typography } from 'antd';
import { ParagraphProps } from 'antd/lib/typography/Paragraph';
import LazyStickerMessage from '../LazyStickerMessage';
import { getThumb, getUrl } from '../../useChatView';
import { useTranslate } from '@tolgee/react';

const MESSAGE_TYPE_KEY_MAP: Record<string, string> = {
  IMAGE: 'domain.message.type.image',
  VIDEO: 'domain.message.type.video',
  STICKER: 'domain.message.type.sticker',
  ITEM: 'domain.message.type.product',
  FAQ_LIVEAGENT: 'domain.message.type.faqLiveAgent',
  VARIATION_CARD: 'domain.message.type.variationCard',
  SYS_ASSIGN: 'domain.message.systemType.assign',
  SYS_WA_REJECT: 'domain.message.systemType.whatsAppRejected',
  SYS_INIT_CONVERSATION: 'scene.chatWorkspace.messageThread.systemMessage.initiatedConversation',
  SYS_FAQ_QUESTION: 'scene.chatWorkspace.messageThread.systemMessage.faqQuestion',
  SHOPEE_RETURN_REFUND_CARD: 'scene.chatWorkspace.messageThread.systemMessage.shopeeReturnRefund',
  recall: 'scene.chatWorkspace.messageThread.systemMessage.recall',
};

const getMessageTypeKey = (type: string): string =>
  MESSAGE_TYPE_KEY_MAP[type] || 'scene.chatWorkspace.messageThread.feedback.unknownMessageType';

const { Paragraph: ParagraphCom } = Typography;

export interface QuoteContentProps {
  quotedMsg: Conversation.QuotedMessage;
  storeName: Conversation.Conversation['storeName'];
  region: Conversation.Message['region'];
  channel: Conversation.Message['channel'];
  conversationId: Conversation.Message['conversationId'];
  ellipsis?: Exclude<ParagraphProps['ellipsis'], boolean | undefined>;
  onClick?: (msgId: string) => void;
}

const Text = styled(ParagraphCom)`
  margin-right: 8px;
  &.ant-typography {
    margin-bottom: 0;
  }
`;
const Box = styled.div<{ from: Conversation.Message['msgFromType'] }>`
  display: flex;
  flex-direction: column;
  padding: 4px;
  border-left: 4px solid;
  border-left-color: ${({ from }) => (from === 'BUYER' ? '#8BCAC4' : '#F5BD96')};
  background: rgba(169, 169, 169, 0.15);
  border-radius: 4px;
  margin-bottom: 8px;
  cursor: pointer;

  .video-box {
    margin-top: 0;
    margin-bottom: 0;
  }
  .quoted-video-msg {
    min-width: auto;
  }
`;

const QuoteContent = (props: QuoteContentProps) => {
  const { quotedMsg, region, channel, conversationId, storeName, ellipsis, onClick } = props;
  const { t } = useTranslate();

  const renderContent = useMemo(() => {
    const { messageType, fromUserName, msgFromType } = quotedMsg;
    let name;
    let textContent;
    let content;

    if (msgFromType) {
      if (msgFromType === 'BUYER') {
        name = fromUserName;
      } else {
        name = fromUserName || storeName;
      }
    }

    switch (messageType) {
      case 'TEXT': {
        textContent = quotedMsg.textContent?.text;
        if (channel === 'SHOPEE') {
          textContent = textContent?.slice(0, 66);
        }
        break;
      }
      case 'STICKER': {
        content = (
          <LazyStickerMessage
            region={region}
            platform={channel}
            dataSource={{
              packageId: quotedMsg.stickerContent?.stickerPackageId || '',
              stickerId: quotedMsg.stickerContent?.stickerId || '',
            }}
          />
        );
        break;
      }
      case 'IMAGE': {
        const url = quotedMsg.imageContent?.externalImgUrl || quotedMsg.imageContent?.imgUrl;
        content = url ? <ImageMessage width={200} src={url} /> : null;
        break;
      }
      case 'TEXT_VIDEO': {
        const videoContent = quotedMsg.textVideoContent?.videoContent;
        content = videoContent ? <VideoBox videoContent={videoContent} /> : null;
        break;
      }
      case 'VIDEO': {
        if (quotedMsg.videoContent) {
          const { thumbUrl, videoUrl } = quotedMsg.videoContent;
          content = (
            <VideoMessage
              className="quoted-video-msg"
              dataSource={{
                thumb: getThumb(channel, region, thumbUrl),
                url: getUrl(channel, region, videoUrl) || '',
              }}
              msg={
                {
                  msgId: quotedMsg.msgId,
                  conversationId,
                  videoContent: quotedMsg.videoContent,
                } as Conversation.Message
              }
            />
          );
        }
        break;
      }
      case 'IMAGE_WITH_TEXT': {
        if (quotedMsg.imageWithTextContent) {
          const { text } = quotedMsg.imageWithTextContent;
          content = `[${t('domain.message.type.image')}]${text}`;
        }
        break;
      }
      default:
        textContent = `[${t(getMessageTypeKey(messageType))}]`;
        break;
    }

    return (
      <>
        <Text ellipsis={{ rows: 2, ...ellipsis }}>{`${name ? `${name}: ` : ''}${
          textContent || ''
        }`}</Text>
        {content}
      </>
    );
  }, [quotedMsg, ellipsis, storeName, region, channel, conversationId, t]);

  return (
    <Box from={quotedMsg.msgFromType} onClick={() => onClick?.(quotedMsg.msgId)}>
      {renderContent}
    </Box>
  );
};

export default QuoteContent;
